import { Box, Text } from '@chakra-ui/react'
import { FC, useCallback, useState } from 'react'
import { OptionsType } from 'react-select'
import { AsyncPaginate } from 'react-select-async-paginate'

import api from '@config/api'

import { FilterProps } from '@base/@types/components/Shared/Filter'

interface IOption {
  label: string
  value: string | number
}

interface SelectAsyncPaginateFromApiPostProps extends FilterProps {
  apiUrl: string
  labelField?: string
  valueField?: string
  idsField?: string
  labelFields?: string[]
}

const SelectAsyncPaginateFromApiPost: FC<SelectAsyncPaginateFromApiPostProps> =
  ({
    label = '',
    value = [],
    isMulti = false,
    isLoading = false,
    isDisabled = false,
    isRequired = false,
    onChange = null,
    limit,
    apiUrl,
    labelField = 'descricao',
    valueField = 'id',
    idsField = 'produtosIds',
    labelFields
  }) => {
    const take = 50
    const [internalLoading, setInternalLoading] = useState(false)
    const [inputValue, setInputValue] = useState('')

    const parseOptions = (data: any[]): IOption[] =>
      data.map(item => ({
        label: labelFields
          ? labelFields.map(f => item[f]).join(' - ')
          : `${item[valueField]} - ${item[labelField]}`,
        value: item[valueField]
      }))

    const parsePastedIds = (text: string): number[] =>
      text
        .split(/[\s,]+/)
        .map(v => Number(v.trim()))
        .filter(v => !isNaN(v) && v > 0)

    const loadOptions = useCallback(
      async (input: string, _loadedOptions: any, { page }: any) => {
        const skip = (page - 1) * take
        setInternalLoading(true)

        try {
          const body = { search: input || undefined, take, skip }
          const response = await api.post(apiUrl, body)
          const total = Number(response.headers['x-total-count'] ?? 0)
          const loaded = (page - 1) * take
          const options = parseOptions(response.data?.data ?? [])

          return {
            options,
            hasMore: loaded + options.length < total,
            additional: { page: page + 1 }
          }
        } catch {
          return { options: [], hasMore: false, additional: { page: page + 1 } }
        } finally {
          setInternalLoading(false)
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [apiUrl, labelField, valueField, labelFields]
    )

    const handleOnChange = (values: IOption | OptionsType<IOption>) => {
      onChange &&
        onChange(
          limit && Array.isArray(values) ? values.slice(0, limit) : values
        )
    }

    const handleInputChange = useCallback(
      async (newInput: string, { action }: any) => {
        if (
          action === 'set-value' ||
          action === 'menu-close' ||
          action === 'input-blur'
        ) {
          setInputValue('')
          return
        }
        if (action !== 'input-change') return

        const pastedIds = parsePastedIds(newInput)

        if (pastedIds.length > 1) {
          // Paste detectado: busca no backend e auto-seleciona
          setInternalLoading(true)
          try {
            const response = await api.post(apiUrl, {
              [idsField]: pastedIds,
              take: pastedIds.length + 10,
              skip: 0
            })
            const fetched = parseOptions(response.data?.data ?? [])

            const current: IOption[] = Array.isArray(value)
              ? (value as IOption[])
              : value
              ? [value as IOption]
              : []
            const existingIds = new Set(current.map(v => String(v.value)))
            const toAdd = fetched.filter(o => !existingIds.has(String(o.value)))

            if (toAdd.length > 0) {
              const merged = [...current, ...toAdd]
              onChange && onChange(limit ? merged.slice(0, limit) : merged)
            }
          } catch {
            // ignore
          } finally {
            setInternalLoading(false)
          }
          setInputValue('') // limpa o campo após auto-seleção
        } else {
          setInputValue(newInput)
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        apiUrl,
        labelField,
        valueField,
        labelFields,
        idsField,
        value,
        onChange,
        limit
      ]
    )

    return (
      <Box w="100%">
        {label && (
          <Text mb={2} fontSize="sm" color="black">
            {label}
          </Text>
        )}
        <AsyncPaginate
          value={value}
          inputValue={inputValue}
          loadOptions={loadOptions}
          debounceTimeout={800}
          additional={{ page: 1 }}
          getOptionValue={option => String(option.value)}
          getOptionLabel={option => option.label}
          classNamePrefix="react-select"
          isSearchable
          isMulti={isMulti}
          onChange={handleOnChange}
          onInputChange={handleInputChange}
          placeholder="Selecione..."
          isDisabled={isDisabled}
          isLoading={isLoading || internalLoading}
          isRequired={isRequired}
          closeMenuOnSelect={!isMulti}
          loadingMessage={() => 'Carregando...'}
          noOptionsMessage={() => 'Nenhum produto encontrado'}
        />
      </Box>
    )
  }

export default SelectAsyncPaginateFromApiPost
